import React, { useState, useEffect, FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  Avatar,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Alert,
  useTheme,
} from '@mui/material'
import axios from 'axios'
import { embedSessionRecovery } from '@/features/anycompass'
import { useLoginMutation } from '@/shared/hooks/queries/useAuthMutations'
import { isCertDomain, API_HOST } from '@/shared/constants/const'

export const LoginPage: FC = () => {
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [errormessage, setErrormessage] = useState<string>('')
  const [certError, setCertError] = useState<boolean>(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useTheme()

  // cert環境ではページマウント時にAPIへの疎通を確認し、
  // TLS拒否（証明書なし）の場合は証明書エラーメッセージを表示する
  useEffect(() => {
    if (!isCertDomain) return

    axios
      .get(`${API_HOST}/api/check_auth/`, {
        withCredentials: true,
        timeout: 5000,
      })
      .catch((error: unknown) => {
        // error.response が無い = TLS層で拒否（証明書なし）
        // error.response がある = HTTP応答あり（401等）= サーバー到達済み、証明書OK
        if (axios.isAxiosError(error) && !error.response && navigator.onLine) {
          setCertError(true)
        }
      })
  }, [])

  // 「login 200 なのに check_auth 401」の文言を出し分けるために渡す
  const loginMutation = useLoginMutation(embedSessionRecovery)

  const submit = async () => {
    setErrormessage('')
    if (!validate()) return

    try {
      const result = await loginMutation.mutateAsync({ email, password })

      // Redux dispatch
      dispatch({
        type: 'setIsLoggedIn',
        isLoggedIn: true,
        appType: result.user.app_type,
      })

      navigate('/')
    } catch (error) {
      // LoginError 型のエラーがスローされる
      const loginError = error as { message: string }
      setErrormessage(loginError.message)
    }
  }

  // 未入力のまま押されたら黙って何もしないのではなく理由を出す。
  // 無言だと「ボタンが壊れている」と読まれ、問い合わせに化ける
  const validate = () => {
    if (!email || !password) {
      setErrormessage('メールアドレスとパスワードを入力してください。')
      return false
    }
    return true
  }

  return (
    <Stack
      height="100vh"
      direction="column"
      spacing={3}
      alignItems="center"
      justifyContent="center"
    >
      <Stack direction="column" alignItems="center" spacing={1}>
        <Avatar />
        <Typography component="h1" variant="h5">
          ログイン
        </Typography>
      </Stack>
      <Box width="400px">
        <form noValidate>
          <Stack direction="column" spacing={3}>
            <Stack direction="column" spacing={1}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label="Emailアドレス"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputLabelProps={{
                  style: { color: theme.palette.secondary.dark },
                }}
              />
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label="パスワード"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputLabelProps={{
                  style: { color: theme.palette.secondary.dark },
                }}
              />
            </Stack>
            <Button
              fullWidth
              variant="contained"
              sx={{
                bgcolor: theme.palette.primary.dark,
                color: '#fff',
              }}
              onClick={submit}
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? 'ログイン中...' : 'ログイン'}
            </Button>
          </Stack>
        </form>
        {certError && (
          <Box pt={2}>
            {/*
              証明書の導入は利用者の作業ではないので、手順ではなく連絡先に寄せる
              (文言の方針は useAuthMutations.ts の toLoginError 手前のコメント)
            */}
            <Alert severity="warning">
              この画面を使うには、端末に証明書の設定が必要です。サポート窓口にご連絡ください。
            </Alert>
          </Box>
        )}
        {errormessage && (
          <Box pt={2}>
            {/*
              文言側が \n を段落の区切りとして使う (ネットワーク断 + 証明書の期限)。
              Alert の既定は white-space: normal で \n が空白 1 つに潰れ、2 文が
              1 行に繋がってしまうため pre-line にする
            */}
            <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>
              {errormessage}
            </Alert>
          </Box>
        )}
      </Box>
    </Stack>
  )
}
